// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuSearch } from "react-icons/lu";
import NewDashboardBox from "../tooltip/NewDashboardBox";
import RailTip from "../tooltip/RailTip";
import { MdKeyboardArrowRight } from "react-icons/md";

const isSameLabel = (a, b) =>
  Boolean(a?.trim()) && a.trim().toLowerCase() === b?.trim()?.toLowerCase();
const DashboardListBar = ({
  dashBoardCategories,
  subscription,
  afterLandDashboard,
  view = false,
  top = 0,
  left = 40,
  onEnter,
  onClose,
  search = false,
  showCategoryLabels = true,
  onPinChange
}) => {
  // Initialize state with null
  const [expandedCategory, setExpandedCategory] = useState(null);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  // Align the panel with the hovered row, pulling it up when it would overflow
  const [offset, setOffset] = useState(top);
  const [subTop, setSubTop] = useState(0);
  const [term, setTerm] = useState("");
  const [isPinned, setIsPinned] = useState(false);

  const setPinned = useCallback(
    (pinned) => {
      setIsPinned(pinned);
      if (onPinChange) onPinChange(pinned);
    },
    [onPinChange]
  );

  const categories = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!search || !needle) return dashBoardCategories || [];
    return (dashBoardCategories || [])
      .map((d) => ({
        ...d,
        dashboardIds: (d?.dashboardIds || []).filter((dash) =>
          dash?.label?.toLowerCase().includes(needle)
        ),
      }))
      .filter((d) => d.dashboardIds.length > 0);
  }, [dashBoardCategories, search, term]);

  const noMatches = Boolean(search && term.trim()) && categories.length === 0;

  useEffect(() => {
    setExpandedCategory(null);
  }, [dashBoardCategories]);

  useEffect(() => {
    const height = panelRef.current?.offsetHeight || 0;
    const maxTop = window.innerHeight - height - 8;
    setOffset(Math.max(8, Math.min(top, maxTop)));
  }, [top, dashBoardCategories, expandedCategory, categories]);

  useEffect(() => {
    if (!isPinned) return;

    const close = () => {
      setTerm("");
      setPinned(false);
      if (onClose) onClose();
    };
    const onMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isPinned, onClose, setPinned]);

  const expandedGroup = dashBoardCategories?.find(
    (d) => d._id === expandedCategory && d.label !== `${subscription.label}_Others`
  );
  const expandedSubItems = expandedGroup?.dashboardIds;
  const expandedLabel = expandedGroup?.label;
  const subOffset = Math.max(0, subTop - offset);

  return (
    <div
      className="dashboard-flyout flex items-start fixed"
      style={{ top: offset, left: left + 2 }}
      ref={containerRef}
      onMouseEnter={() => {
        if (onEnter) onEnter();
      }}
      onMouseLeave={() => {
        if (isPinned) {
          if (onEnter) onEnter();
          return;
        }
        setExpandedCategory(null);
        setTerm("");
        if (onClose) onClose();
      }}
    >
      {/* Default View (view=false) */}
      {!view && (
        <div ref={panelRef} className="font-geist flex flex-col z-40 w-72 max-h-[calc(100vh-16px)] pb-1 bg-card-card rounded-lg shadow-xl outline outline-1 outline-general-border overflow-hidden">
          {showCategoryLabels && Boolean(subscription?.label?.trim()) && (
            <RailTip title={subscription.label} whenTruncated>
              <p className="flex h-9 shrink-0 items-center border-b border-general-border px-3 truncate text-sm font-medium text-general-foreground">
                {subscription.label}
              </p>
            </RailTip>
          )}
          {search && (
            <>
              <div className="self-stretch px-3 py-2 flex justify-start items-center gap-2">
                <LuSearch className="size-4 shrink-0 text-general-muted-foreground" />
                <input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onFocus={() => setPinned(true)}
                  placeholder="Search"
                  className="flex-1 min-w-0 bg-transparent text-general-foreground placeholder:text-general-muted-foreground text-sm font-normal leading-5 outline-none"
                />
              </div>
              <div className="self-stretch h-px shrink-0 bg-general-border" />
            </>
          )}
          <div
            className={`app-scrollbar ${search ? "py-1" : ""} flex flex-col justify-start items-start overflow-y-auto overscroll-contain`}
          >
            {noMatches && (
              <div className="self-stretch px-3 py-2 text-left text-general-muted-foreground text-sm font-normal leading-5">
                {"No dashboards found"}
              </div>
            )}
            {categories?.map((d) => (
              <div className="self-stretch flex flex-col justify-start items-start" key={d?._id}>
                {showCategoryLabels &&
                  Boolean(d?.label?.trim()) &&
                  !isSameLabel(d?.label, subscription?.label) && (
                  <RailTip title={d?.label} whenTruncated>
                    <div className="flex h-8 w-full shrink-0 items-center border-b border-general-border px-3 truncate text-xs font-medium text-general-muted-foreground">
                      {d?.label}
                    </div>
                  </RailTip>
                )}
                {d?.dashboardIds?.map((dashboard) => (
                  <NewDashboardBox
                    key={dashboard?._id}
                    afterlandDashboardNew={afterLandDashboard}
                    dashboard={dashboard}
                    categoryLabel={d?.label}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {view && (
        <div ref={panelRef} className="font-geist flex flex-col z-[1000] w-72 max-h-[calc(100vh-16px)] pb-1 bg-card-card rounded-lg shadow-xl outline outline-1 outline-general-border">
          {/* Subscription Label Container */}
          <RailTip title={subscription.label} whenTruncated>
            <p className="flex h-9 shrink-0 items-center border-b border-general-border px-3 truncate text-sm font-medium text-general-foreground">
              {subscription.label}
            </p>
          </RailTip>

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto overscroll-contain overflow-x-hidden flex flex-col justify-start items-start scrollbar-hide">
            {dashBoardCategories?.map((d, index) => {
              // Skip the navigation label for "Others" category
              if (d.label === `${subscription.label}_Others`) {
                return (
                  <div
                    key={d._id}
                    className="self-stretch flex flex-col justify-start items-start"
                    onMouseEnter={() => setExpandedCategory(null)}
                  >
                    {/* Directly show sub-items without parent item */}
                      {d.dashboardIds?.map((dashboard) => (
                        <NewDashboardBox
                          key={dashboard?._id}
                          afterlandDashboardNew={afterLandDashboard}
                          dashboard={dashboard}
                          view={true}
                          categoryLabel={d?.label}
                        />
                      ))}

                  </div>
                );
              }

              const isExpanded = expandedCategory === d._id;

              return (
                <div key={d._id} className="self-stretch flex flex-col justify-start items-start">
                  {/* Main Category Item */}
                  <div
  className={`${isExpanded ? "bg-general-accent" : ""} self-stretch min-h-8 px-2 py-1.5 rounded-md hover:bg-general-accent ani flex cursor-pointer justify-start items-center gap-2`}
  onMouseEnter={(e) => {
    if (d.dashboardIds?.length > 0) {
      setSubTop(e.currentTarget.getBoundingClientRect().top);
      setExpandedCategory(d._id);
    } else {
      setExpandedCategory(null);
    }
  }}
>
                    {/* Label */}
                    <span className="flex-1 flex justify-start items-center gap-1 text-general-foreground text-sm font-normal leading-5 text-left">
{d.label !== "Competitive Intelligence" ? (
  d.label
) : (
  <>
    {d.label}
    <img
      className="h-5 mx-2 cursor-pointer"
      alt="tag"
      src="https://hwlobbystorer1.blob.core.windows.net/images/new_h-13-dark.svg"
    />
  </>
)}
</span>

                    {/* Arrow for expandable items */}
                    {d.dashboardIds?.length > 0 && (
                      <div className="w-5 p-0.5 flex flex-col justify-center items-center shrink-0">
                        <MdKeyboardArrowRight className="size-4 text-general-muted-foreground" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view && expandedSubItems?.length > 0 && (
        <div className="pl-2" style={{ marginTop: subOffset }}>
          <div className="font-geist flex flex-col z-[1000] w-72 max-h-[calc(100vh-16px)] pb-1 bg-card-card rounded-lg shadow-xl outline outline-1 outline-general-border">
            {Boolean(expandedLabel?.trim()) && (
              <div className="self-stretch px-3 pb-1 pt-2 flex justify-start items-center gap-2">
                <RailTip title={expandedLabel} whenTruncated>
                  <p className="min-w-0 flex-1 truncate text-left text-general-muted-foreground text-xs font-medium leading-4">
                    {expandedLabel}
                  </p>
                </RailTip>
              </div>
            )}
            <div className="flex-1 overflow-y-auto overscroll-contain overflow-x-hidden flex flex-col justify-start items-start scrollbar-hide">
              {expandedSubItems.map((dashboard) => (
                <NewDashboardBox
                  key={dashboard?._id}
                  afterlandDashboardNew={afterLandDashboard}
                  dashboard={dashboard}
                  view={true}
                  categoryLabel={expandedLabel}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DashboardListBar);
